import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';

import useAuthenticationOptions from './useAuthenticationOptions';

const OFFER = {
  assignment: null,
  locked: false,
  provider: null,
  requireSso: false,
  options: [
    { value: 'paccar', label: 'PCAR (Pass)' },
    { value: 'password', label: 'Password' },
  ],
};

const LOCKED = {
  assignment: 'domain',
  locked: true,
  provider: 'decisiv',
  requireSso: false,
  options: [{ value: 'decisiv', label: 'Decisiv Okta' }],
};

let payload;
const onChange = jest.fn();

// Reports what the hook resolved, so the assertions read off the DOM rather than
// reaching into the hook.
function Probe({ accountIds, email, value }) {
  const {
    authentication,
    isAskable,
    isLoadingAuthentication,
  } = useAuthenticationOptions({
    accountIds,
    email,
    onChange,
    value,
  });

  return (
    <>
      <span data-testid="values">
        {authentication.options.map(({ value: v }) => v).join(',')}
      </span>
      <span data-testid="locked">{String(authentication.locked)}</span>
      <span data-testid="loading">{String(isLoadingAuthentication)}</span>
      <span data-testid="askable">{String(isAskable)}</span>
    </>
  );
}

const values = () => screen.getByTestId('values').textContent;
const locked = () => screen.getByTestId('locked').textContent;
const askable = () => screen.getByTestId('askable').textContent;

const renderProbe = (props = {}) =>
  render(
    <Probe accountIds={['7']} email="jane@example.com" value="" {...props} />,
  );

/** Waits for the fetch the hook fires on mount to settle. */
const settle = () =>
  waitFor(() =>
    expect(screen.getByTestId('loading').textContent).toBe('false'),
  );

beforeEach(() => {
  onChange.mockClear();
  payload = OFFER;
  global.fetch = jest
    .fn()
    .mockImplementation(() =>
      Promise.resolve({ ok: true, json: async () => payload }),
    );
});

describe('management/Users useAuthenticationOptions', () => {
  it('asks the server what the accounts and email allow', async () => {
    renderProbe();

    await waitFor(() => expect(values()).toBe('paccar,password'));

    const [url] = global.fetch.mock.calls[0];
    expect(url).toContain('/management/users/authentication_options');
    expect(url).toContain('email=jane%40example.com');
    expect(url).toContain('account_ids%5B%5D=7');
  });

  it('sends every chosen account', async () => {
    renderProbe({ accountIds: ['7', '8'] });

    await settle();

    const [url] = global.fetch.mock.calls[0];
    expect(url).toContain('account_ids%5B%5D=7');
    expect(url).toContain('account_ids%5B%5D=8');
  });

  // Reported as its own state, so the step can disable the field and say the email
  // is what is missing, rather than opening onto an empty list or blaming the
  // accounts for offering nothing.
  describe('what it will not ask about', () => {
    it('waits for a well-formed email', async () => {
      renderProbe({ email: 'nope' });

      await settle();

      expect(global.fetch).not.toHaveBeenCalled();
      expect(values()).toBe('');
      expect(askable()).toBe('false');
    });

    it('waits for an email at all', async () => {
      renderProbe({ email: '' });

      await settle();

      expect(global.fetch).not.toHaveBeenCalled();
      expect(askable()).toBe('false');
    });

    it('waits for at least one account', async () => {
      renderProbe({ accountIds: [] });

      await settle();

      expect(global.fetch).not.toHaveBeenCalled();
      expect(askable()).toBe('false');
    });

    it('reports it can ask once both are there', async () => {
      renderProbe();

      await settle();

      expect(askable()).toBe('true');
    });
  });

  it('re-asks when the accounts change', async () => {
    const { rerender } = renderProbe();
    await settle();

    rerender(
      <Probe accountIds={['7', '9']} email="jane@example.com" value="" />,
    );

    await waitFor(() => expect(global.fetch).toHaveBeenCalledTimes(2));
  });

  it('does not re-ask when the same accounts arrive in a new array', async () => {
    const { rerender } = renderProbe();
    await settle();

    rerender(<Probe accountIds={['7']} email="jane@example.com" value="" />);

    await settle();
    expect(global.fetch).toHaveBeenCalledTimes(1);
  });

  // A domain rule is not the admin's to override, so the draft follows the server.
  describe('a locked domain', () => {
    beforeEach(() => {
      payload = LOCKED;
    });

    it('reports the field as locked', async () => {
      renderProbe();

      await waitFor(() => expect(locked()).toBe('true'));
    });

    it('puts the assigned provider into the draft', async () => {
      renderProbe();

      await waitFor(() => expect(onChange).toHaveBeenCalledWith('decisiv'));
    });

    it('leaves a draft already holding it alone', async () => {
      renderProbe({ value: 'decisiv' });

      await settle();
      expect(onChange).not.toHaveBeenCalled();
    });
  });

  // Going back to step 1 and changing accounts can withdraw the option already
  // chosen. Submitting it anyway is a create the server refuses.
  describe('an option that is no longer offered', () => {
    it('clears the draft', async () => {
      renderProbe({ value: 'decisiv' });

      await waitFor(() => expect(onChange).toHaveBeenCalledWith(''));
    });

    it('keeps a draft the offer still contains', async () => {
      renderProbe({ value: 'paccar' });

      await settle();
      expect(onChange).not.toHaveBeenCalled();
    });

    it('keeps an empty draft empty rather than reporting a change', async () => {
      renderProbe();

      await settle();
      expect(onChange).not.toHaveBeenCalled();
    });
  });

  describe('when the request fails', () => {
    beforeEach(() => {
      global.fetch = jest.fn().mockRejectedValue(new Error('offline'));
    });

    // Nothing on offer means the required field cannot be answered, which is the
    // honest outcome: only the server knows what this user may sign in with.
    it('offers nothing rather than guessing', async () => {
      renderProbe();

      await settle();
      expect(values()).toBe('');
    });

    it('stops loading', async () => {
      renderProbe();

      await settle();
    });
  });

  // The wizard cannot crash over a payload it did not expect: the step maps over
  // the option list to build its select.
  it('survives a response with no option list', async () => {
    payload = {};
    renderProbe();

    await settle();
    expect(values()).toBe('');
    expect(locked()).toBe('false');
  });

  // The admin can retype the email faster than the answer comes back.
  it('ignores an answer overtaken by a newer request', async () => {
    let resolveFirst;
    global.fetch = jest
      .fn()
      .mockImplementationOnce(
        () =>
          new Promise((resolve) => {
            resolveFirst = resolve;
          }),
      )
      .mockImplementation(() =>
        Promise.resolve({ ok: true, json: async () => LOCKED }),
      );

    const { rerender } = renderProbe();

    rerender(<Probe accountIds={['9']} email="jane@example.com" value="" />);

    await waitFor(() => expect(locked()).toBe('true'));

    resolveFirst({ ok: true, json: async () => OFFER });

    await settle();
    expect(locked()).toBe('true');
  });
});
